import { getSafeApiUrl, baseUrlIncludesApi, config } from '../config/environment';

/**
 * API URL 검증 및 수정을 위한 유틸리티 클래스
 */
export class ApiUrlValidator {
  /**
   * Base URL과 엔드포인트를 조합하여 최종 URL을 생성
   */
  static buildApiUrl(baseUrl: string, endpoint: string): string {
    // Base URL 정규화 (끝에 슬래시 제거)
    const normalizedBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
    
    // 엔드포인트 정규화 (앞에 슬래시 추가)
    const normalizedEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
    
    // Base URL에 /api가 포함되어 있는지 확인
    if (baseUrlIncludesApi(normalizedBaseUrl)) {
      // Base URL에 이미 /api가 있으면 엔드포인트에 /api 추가하지 않음
      return `${normalizedBaseUrl}${normalizedEndpoint}`;
    } else {
      // Base URL에 /api가 없으면 엔드포인트 앞에 /api 추가
      return `${normalizedBaseUrl}/api${normalizedEndpoint}`;
    }
  }

  /**
   * URL에서 /api 중복을 검출
   */
  static detectApiDuplication(url: string): { hasDuplication: boolean; duplicatedParts: string[] } {
    const apiMatches = url.match(/\/api/g);
    const hasDuplication = apiMatches && apiMatches.length > 1;
    
    return {
      hasDuplication: !!hasDuplication,
      duplicatedParts: hasDuplication ? apiMatches || [] : []
    };
  }

  /**
   * URL에서 /api 누락을 검출
   */
  static detectApiMissing(url: string, expectedApiPath: boolean = true): { isMissing: boolean; suggestion: string } {
    const hasApi = url.includes('/api');
    
    if (expectedApiPath && !hasApi) {
      // /api가 있어야 하는데 없는 경우
      const suggestion = url.includes('localhost') 
        ? url.replace('localhost', 'localhost/api')
        : url.replace('judam.kr', 'judam.kr/api');
      
      return { isMissing: true, suggestion };
    }
    
    return { isMissing: false, suggestion: url };
  }

  /**
   * 현재 환경의 모든 API URL을 검증
   */
  static validateAllApiUrls(): {
    gateway: ValidationResult;
    inventory: ValidationResult;
    order: ValidationResult;
    identity: ValidationResult;
    notification: ValidationResult;
  } {
    const services = ['GATEWAY', 'INVENTORY', 'ORDER', 'IDENTITY', 'NOTIFICATION'] as const;
    const results: any = {};

    services.forEach(service => {
      const baseUrl = getSafeApiUrl(service);
      const hasApiInBase = baseUrlIncludesApi(baseUrl);
      
      // 테스트 엔드포인트로 검증
      const testEndpoint = '/test';
      const fullUrl = this.buildApiUrl(baseUrl, testEndpoint);
      const duplication = this.detectApiDuplication(fullUrl);
      const missing = this.detectApiMissing(fullUrl, true);

      results[service.toLowerCase()] = {
        baseUrl,
        hasApiInBase,
        testUrl: fullUrl,
        duplication,
        missing,
        isValid: !duplication.hasDuplication && !missing.isMissing,
        suggestions: []
      };

      // 문제가 있으면 수정 제안 추가
      if (duplication.hasDuplication) {
        results[service.toLowerCase()].suggestions.push(
          `Base URL에서 /api 제거: ${baseUrl} → ${baseUrl.replace('/api', '')}`
        );
      }
      if (missing.isMissing) {
        results[service.toLowerCase()].suggestions.push(
          `Base URL에 /api 추가: ${baseUrl} → ${baseUrl}/api`
        );
      }
    });

    return results;
  }

  /**
   * 특정 엔드포인트에 대한 URL 검증
   */
  static validateEndpoint(service: 'GATEWAY' | 'INVENTORY' | 'ORDER' | 'IDENTITY' | 'NOTIFICATION', endpoint: string): ValidationResult {
    const baseUrl = getSafeApiUrl(service);
    const fullUrl = this.buildApiUrl(baseUrl, endpoint);
    const duplication = this.detectApiDuplication(fullUrl);
    const missing = this.detectApiMissing(fullUrl, true);

    return {
      baseUrl,
      hasApiInBase: baseUrlIncludesApi(baseUrl),
      testUrl: fullUrl,
      duplication,
      missing,
      isValid: !duplication.hasDuplication && !missing.isMissing,
      suggestions: []
    };
  }

  /**
   * 환경별 URL 패턴 검증
   */
  static validateEnvironmentPatterns(): EnvironmentValidationResult {
    const isDev = config.ENVIRONMENT === 'development';
    const patterns = {
      development: {
        gateway: 'http://localhost:5000/api',
        inventory: 'http://localhost:5001/api',
        order: 'http://localhost:5192/api',
        identity: 'http://localhost:5007/api',
        notification: 'http://localhost:5004/api'
      },
      production: {
        // 프로덕션에서는 현재 호스트명을 기준으로 /api를 붙인 주소가 되어야 함
        gateway: `https://${window.location.hostname}/api`,
        inventory: `https://${window.location.hostname}/api`,
        order: `https://${window.location.hostname}/api`,
        identity: `https://${window.location.hostname}/api`,
        notification: `https://${window.location.hostname}/api`
      }
    };

    const currentPatterns = isDev ? patterns.development : patterns.production;
    const validationResults: any = {};

    Object.entries(currentPatterns).forEach(([service, expectedPattern]) => {
      const actualUrl = getSafeApiUrl(service.toUpperCase() as any);
      const isValid = actualUrl === expectedPattern;
      
      validationResults[service] = {
        expected: expectedPattern,
        actual: actualUrl,
        isValid,
        matches: actualUrl === expectedPattern
      };
    });

    return {
      environment: config.ENVIRONMENT,
      patterns: validationResults,
      allValid: Object.values(validationResults).every((r: any) => r.isValid)
    };
  }

  /**
   * 브라우저 콘솔에서 사용할 수 있는 검증 함수들
   */
  static getConsoleHelpers() {
    return {
      validateAll: () => {
        console.log('🔍 모든 API URL 검증 결과:');
        console.table(this.validateAllApiUrls());
      },
      validateEnvironment: () => {
        console.log('🌍 환경별 패턴 검증 결과:');
        console.table(this.validateEnvironmentPatterns());
      },
      validateEndpoint: (service: string, endpoint: string) => {
        console.log(`🔗 ${service} 서비스의 ${endpoint} 엔드포인트 검증:`);
        console.table(this.validateEndpoint(service.toUpperCase() as any, endpoint));
      },
      testUrl: (baseUrl: string, endpoint: string) => {
        const result = this.buildApiUrl(baseUrl, endpoint);
        console.log(`🔗 URL 조합 테스트:`);
        console.log(`Base URL: ${baseUrl}`);
        console.log(`Endpoint: ${endpoint}`);
        console.log(`결과: ${result}`);
        console.log(`중복 검출:`, this.detectApiDuplication(result));
        console.log(`누락 검출:`, this.detectApiMissing(result, true));
      },
      // 효율적인 빠른 검증 (리소스 최소화)
      quickValidate: () => {
        console.log('⚡ 빠른 API URL 검증 실행...');
        const startTime = performance.now();
        
        try {
          const services = ['GATEWAY', 'INVENTORY', 'ORDER', 'IDENTITY', 'NOTIFICATION'] as const;
          const issues: string[] = [];
          
          services.forEach(service => {
            const baseUrl = getSafeApiUrl(service);
            
            // 빠른 검증: Base URL에 /api가 있는지 확인
            if (!baseUrlIncludesApi(baseUrl)) {
              issues.push(`${service}: Base URL에 /api 누락 - ${baseUrl}`);
            }
            
            // 빠른 검증: 올바른 프로토콜 사용 확인
            if (!baseUrl.startsWith('http://') && !baseUrl.startsWith('https://')) {
              issues.push(`${service}: 잘못된 프로토콜 - ${baseUrl}`);
            }
          });
          
          const endTime = performance.now();
          const duration = (endTime - startTime).toFixed(2);
          
          if (issues.length === 0) {
            console.log(`✅ 빠른 검증 완료 (${duration}ms) - 문제 없음`);
          } else {
            console.warn(`⚠️ 빠른 검증 완료 (${duration}ms) - ${issues.length}개 문제 발견:`);
            issues.forEach(issue => console.warn(`  - ${issue}`));
          }
          
          return { issues, duration: parseFloat(duration) };
        } catch (error) {
          console.error('❌ 빠른 검증 중 오류:', error);
          return { issues: [`검증 오류: ${error}`], duration: 0 };
        }
      },
      // 개발 모드에서만 실행되는 자동 검증
      autoValidate: () => {
        // 개발 모드가 아니면 실행하지 않음
        if (process.env.NODE_ENV !== 'development') {
          return;
        }
        
        // 이미 검증이 실행되었는지 확인 (중복 실행 방지)
        if ((window as any).__apiUrlValidated) {
          return;
        }
        
        console.log('🔧 개발 모드 자동 검증 실행...');
        const result = this.getConsoleHelpers().quickValidate();
        
        if (result.issues.length > 0) {
          console.warn('⚠️ API URL 설정에 문제가 있습니다. 자세한 검증을 위해 다음을 실행하세요:');
          console.warn('window.apiUrlValidator.validateAll()');
        }
        
        // 검증 완료 표시
        (window as any).__apiUrlValidated = true;
        
        return result;
      }
    };
  }
}

/**
 * 검증 결과 타입 정의
 */
export interface ValidationResult {
  baseUrl: string;
  hasApiInBase: boolean;
  testUrl: string;
  duplication: { hasDuplication: boolean; duplicatedParts: string[] };
  missing: { isMissing: boolean; suggestion: string };
  isValid: boolean;
  suggestions: string[];
}

export interface EnvironmentValidationResult {
  environment: string;
  patterns: Record<string, {
    expected: string;
    actual: string;
    isValid: boolean;
    matches: boolean;
  }>;
  allValid: boolean;
}

// 브라우저 콘솔에서 사용할 수 있도록 전역 객체에 추가
if (typeof window !== 'undefined') {
  (window as any).apiUrlValidator = ApiUrlValidator.getConsoleHelpers();
  console.log('🔧 API URL 검증 도구가 로드되었습니다. 브라우저 콘솔에서 window.apiUrlValidator를 사용하세요.');
} 